import { DocumentReference, collection, doc } from 'firebase/firestore';
import { MetricGroup, Metric, EntityLabel, EmissionFactor, Scope } from '@esg/esg-global-types';
import { apollo_client, BatchWrite } from '../app/db_util';
import { generateUuidV4 } from '../../util/uuid';
import { MetadataError } from '@ep/error-handling';
import { validateMasterListParams } from '../../util/validation';
import { auth, db } from '../google/firebase';
import {
  ALLOW_DELETE_METRIC_QUERY,
  GET_METRIC_BY_NAME_QUERY,
  GET_METRICS_QUERY,
  GET_UNCONFIGURED_METRICS_QUERY
} from '../graphql/queries/metric_capture/metric';
import {
  CREATE_METRICS_MUTATOR,
  DELETE_METRICS_MUTATOR,
  DELETE_METRICS_WITH_EMISSION_FACTORS_MUTATOR,
  SYNC_MASTER_LIST_METRICS,
  UNDELETE_METRICS_WITH_EMISSION_FACTORS_MUTATOR,
  UPDATE_METRICS_MUTATOR
} from '../graphql/mutations/metric_capture/metric';

export interface MetricExtended extends Omit<Metric, 'scope'> {
  metric_group: MetricGroup;
  scope: Scope | null;
}

export interface StandardNameToReferenceMap {
  [name: string]: DocumentReference;
}
export interface MetricGroupNameToIdMap {
  [name: string]: string;
}

export interface MetricData extends Omit<Metric, 'id' | 'scope'> {
  metric_group?: MetricGroup;
  scope?: Scope | null;
}

// Singular and plural label for model entity.
export const metric_label: EntityLabel = {
  one: 'Metric',
  many: 'Metrics'
};

/**
 * Creates a database reference for a Metric entity, either master list or for a configured company.
 * @param {string | undefined} id Id for a specific document. An undefined value is used to reference the collection to create a new document.
 * @param {string} metric_group_id The metric group to which a metric belongs or should belong.
 * @param {string} group_id When not referencing a master list document or collection, a group id for the configured company is required.
 * @param {string} company_id When not referencing a master list document or collection, a company id for the configured company is required.
 * @returns {DocumentReference} The reference of the document or collection.
 */
export const refMetric = (
  master_list: boolean,
  id: string | undefined,
  metric_group_id: string,
  group_id?: string,
  company_id?: string
): DocumentReference => {
  const collection_path: string = createMetricCollectionPath(
    master_list,
    metric_group_id,
    group_id,
    company_id
  );
  return id ? doc(collection(db, collection_path), id) : doc(collection(db, collection_path));
};

/**
 * Create a collection path string for the master list metrics or configured metrics.
 * @param {boolean} master_list Reference the master list collection.
 * @param {string} metric_group_id Metric group id to which a metric belongs.
 * @param {string} group_id When not referencing a master list collection, a group id for the configured company is required.
 * @param {string} company_id When not referencing a master list collection, a company id for the configured company is required.
 * @returns {string} Path to a metrics collection.
 */
export const createMetricCollectionPath = (
  master_list: boolean,
  metric_group_id: string,
  group_id?: string,
  company_id?: string
): string => {
  try {
    validateMasterListParams(master_list, group_id, company_id);
    return master_list
      ? `/metric_group_master_list/${metric_group_id}/metrics`
      : `groups/${group_id}/companies/${company_id}/metric_groups/${metric_group_id}/metrics`;
  } catch (err: unknown) {
    const tracking_id: string = generateUuidV4();
    throw new MetadataError(
      err instanceof Error
        ? err.message
        : 'Error: getMetricCollectionPath failed on an unknown error.',
      {
        master_list: master_list,
        group_id: group_id,
        company_id: company_id
      },
      tracking_id
    );
  }
};

/**
 * Query all metric documents for specified group and company joined with relative Metric Group, and optional Standard, data.
 * @param {boolean} master_list Return master list or configured metric documents.
 * @param {string} group_id ID of configured group.
 * @param {string} company_id ID of configured company.
 * @param {Array<string>} standard_ids Query metrics within specified Standard id's.
 * @param {Array<string>} metric_group_ids Query metrics within specified Metric Group ids.
 * @param {boolean} get_relative_standard Optionally query relative Standard data associated with each Metric.
 * @returns {Promise<Array<MetricExtended>>}
 */
export const getMetrics = async (
  master_list: boolean,
  group_id?: string,
  company_id?: string,
  standard_ids?: Array<string>,
  get_relative_standard?: boolean,
  metric_group_ids?: Array<string>
): Promise<Array<MetricExtended>> => {
  try {
    validateMasterListParams(master_list, group_id, company_id);
    const metrics: Array<MetricExtended> = await apollo_client
      .query({
        variables: {
          master_list: master_list,
          group_id: group_id,
          company_id: company_id,
          standard_ids: standard_ids,
          metric_group_ids: metric_group_ids,
          get_relative_standard: get_relative_standard
        },
        query: GET_METRICS_QUERY,
        fetchPolicy: 'no-cache'
      })
      .then((response) => {
        return response.data.metrics;
      });
    return metrics;
  } catch (err: unknown) {
    const tracking_id: string = generateUuidV4();
    throw new MetadataError(
      err instanceof Error ? err.message : 'Error: getMetrics failed on an unknown error.',
      {
        group_id: group_id,
        company_id: company_id
      },
      tracking_id
    );
  }
};

/**
 * Query all unconfigured metric documents for specified group and company joined with an optional relative Standard object.
 * @param {string} group_id ID of configured group.
 * @param {string} company_id ID of configured company.
 * @param {Array<string>} standard_ids Query metrics within specified Standard id's.
 * @param {boolean} get_relative_standard Optionally query relative Standard data associated with each Metric.
 * @returns {Promise<Array<MetricExtended>>}
 */
export const getUnconfiguredMetrics = async (
  group_id: string,
  company_id: string,
  standard_ids?: Array<string>,
  get_relative_standard?: boolean
): Promise<Array<MetricExtended>> => {
  try {
    const unconfigured_metrics: Array<MetricExtended> = await apollo_client
      .query({
        variables: {
          group_id: group_id,
          company_id: company_id,
          standard_ids: standard_ids,
          get_relative_standard: get_relative_standard
        },
        query: GET_UNCONFIGURED_METRICS_QUERY,
        fetchPolicy: 'no-cache'
      })
      .then((response) => {
        return response.data.unconfigured_metrics;
      });
    return unconfigured_metrics;
  } catch (err: unknown) {
    const tracking_id: string = generateUuidV4();
    throw new MetadataError(
      err instanceof Error
        ? err.message
        : 'Error: getUnconfiguredMetrics failed on an unknown error.',
      {
        group_id: group_id,
        company_id: company_id
      },
      tracking_id
    );
  }
};

/**
 * Create Metric data in firestore on master list or company level
 * @param {boolean} master_list Flag to create new Metric within master list or company level collection
 * @param {Array<MetricExtended | MetricData>} metrics list of new metrics to be added to the database.
 * @param {string | undefined} group_id Optional ID of Group to create metrics for.
 * @param {string | undefined} company_id Optional ID of Company to create metrics for.
 * @param {boolean | undefined} include_emission_factors Optionally specify whether to include emission factor creates with the metrics, defaults to true.
 * @returns {Promise<Array<BatchWrite>>}
 */
export const createMetrics = async (
  master_list: boolean,
  metrics: Array<MetricExtended | MetricData>,
  group_id?: string,
  company_id?: string,
  include_emission_factors?: boolean
): Promise<Array<BatchWrite>> => {
  // TODO: Add check to ensure we cannot add a metric with a name that already exist.
  try {
    {
      const metric_writes: Array<BatchWrite> = await apollo_client
        .mutate({
          mutation: CREATE_METRICS_MUTATOR,
          variables: {
            master_list: master_list,
            metrics: metrics,
            user_email: auth.currentUser?.email,
            group_id: group_id,
            company_id: company_id,
            include_emission_factors: include_emission_factors
          }
        })
        .then((response) => {
          return response.data.createMetrics;
        });
      return metric_writes;
    }
  } catch (err: unknown) {
    const tracking_id: string = generateUuidV4();
    throw new MetadataError(
      err instanceof Error
        ? err.message
        : 'Error: lib/metric_capture/metric.ts failed on an unknown error while calling createMetrics.',
      {
        master_list: master_list,
        metrics: metrics,
        group_id,
        company_id
      },
      tracking_id
    );
  }
};

/**
 * Update metric with relative data
 * @param {boolean} master_list Flag to update metric in master list
 * @param {MetricExtended} updated_metric New metric data to push into document
 * @param {MetricExtended} original_metric Old metric data
 * @param {string} group_id Optional ID of Group to update metric for
 * @param {string} company_id Optional ID of Company to update metric for
 * @returns {void}
 */
export const updateMetric = async (
  master_list: boolean,
  updated_metric: MetricExtended,
  original_metric: MetricExtended,
  group_id?: string,
  company_id?: string
): Promise<void> => {
  try {
    await apollo_client
      .mutate({
        mutation: UPDATE_METRICS_MUTATOR,
        variables: {
          master_list: master_list,
          updated_metric: updated_metric,
          original_metric: original_metric,
          group_id: group_id,
          company_id: company_id,
          user_email: auth.currentUser?.email
        }
      })
      .then((response) => {
        return response.data.updateMetric;
      });
  } catch (err: unknown) {
    const tracking_id: string = generateUuidV4();
    throw new MetadataError(
      err instanceof Error
        ? err.message
        : 'Error: lib/metric_capture/metric.ts failed on an unknown error while calling updateMetric.',
      {
        group_id: group_id,
        company_id: company_id,
        updated_metric: updated_metric,
        original_metric: original_metric
      },
      tracking_id
    );
  }
};

/**
 * Soft delete a Metric.
 * @param {string} group_id ID of Group to delete metric for
 * @param {string} company_id ID of Company to delete metric for
 * @param {string} metric_id ID of metric to delete
 * @param {string} metric_group_id ID of metric group to find delete metric in
 * @returns {void}
 */
export const deleteMetric = async (
  group_id: string,
  company_id: string,
  metric_id: string,
  metric_group_id: string,
  metric_name: string
): Promise<void> => {
  try {
    await apollo_client
      .mutate({
        mutation: DELETE_METRICS_MUTATOR,
        variables: {
          group_id: group_id,
          company_id: company_id,
          metric_id: metric_id,
          metric_group_id: metric_group_id,
          metric_name: metric_name,
          user_email: auth.currentUser?.email
        }
      })
      .then((response) => {
        return response.data.deleteMetric;
      });
  } catch (err: unknown) {
    const tracking_id: string = generateUuidV4();
    throw new MetadataError(
      err instanceof Error
        ? err.message
        : 'Error: lib/metric_capture/metric.ts failed on an unknown error while calling deleteMetric.',
      {
        group_id: group_id,
        company_id: company_id,
        metric_id: metric_id,
        metric_group_id: metric_group_id,
        metric_name: metric_name
      },
      tracking_id
    );
  }
};

/**
 * Soft delete Metric and any nested Emission Factors in Firestore transaction
 * @param {boolean} master_list Flag to delete entities from their respective master lists
 * @param {string | undefined} group_id Optional Group to delete metric from
 * @param {string | undefined} company_id Optional Company to delete metric from
 * @param {string} metric_id ID of metric to delete
 * @param {string} metric_group_id ID of metric group to find metric in
 * @param {string} metric_name Name of metric to delete
 * @return {void}
 */
export const deleteMetricWithEmissionFactors = async (
  master_list: boolean,
  metric_id: string,
  metric_group_id: string,
  metric_name: string,
  group_id?: string,
  company_id?: string
): Promise<void> => {
  try {
    await apollo_client
      .mutate({
        mutation: DELETE_METRICS_WITH_EMISSION_FACTORS_MUTATOR,
        variables: {
          master_list: master_list,
          metric_id: metric_id,
          metric_group_id: metric_group_id,
          metric_name: metric_name,
          group_id: group_id,
          company_id: company_id,
          user_email: auth.currentUser?.email
        }
      })
      .then((response) => {
        return response.data.deleteMetricWithEmissionFactors;
      });
  } catch (err: unknown) {
    const tracking_id: string = generateUuidV4();
    throw new MetadataError(
      err instanceof Error
        ? err.message
        : 'Error: lib/metric_capture/metric.ts failed on an unknown error while calling deleteMetricWithEmissionFactors.',
      {
        group_id: group_id,
        company_id: company_id,
        metric_id: metric_id,
        metric_group_id: metric_group_id,
        metric_name: metric_name
      },
      tracking_id
    );
  }
};

/**
 * Check if a Metric can be safely deleted.
 * @param {string} group_id ID of Group to check Metric for
 * @param {string} company_id ID of Company to check Metric for
 * @param {string} metric_id ID of metric to check
 * @returns {boolean}
 */
export const allowDeleteMetric = async (
  group_id: string,
  company_id: string,
  metric_id: string,
  metric_group_id: string
): Promise<boolean> => {
  try {
    const allow_delete: boolean = await apollo_client
      .query({
        variables: {
          group_id: group_id,
          company_id: company_id,
          metric_id: metric_id,
          metric_group_id: metric_group_id
        },
        query: ALLOW_DELETE_METRIC_QUERY,
        fetchPolicy: 'no-cache'
      })
      .then((response) => {
        return response.data.allowDeleteMetric;
      });
    return allow_delete;
  } catch (err) {
    const error = `Error while checking Metric for deletion: ${JSON.stringify({
      message: err instanceof Error ? err.message : '',
      stacktrace: err instanceof Error ? err.stack : ''
    })}`;
    throw new Error(`Error: allowDeleteMetric: ${JSON.stringify(error)}.`);
  }
};

/**
 * Query a metric by metric name and metric group name.
 * @param {boolean} master_list Return master list or configured metric documents.
 * @param {string} metric_group_name Name of the metric's group.
 * @param {string} metric_name Name of the metric to be queried.
 * @param {string} group_id When not referencing a master list document or collection, a group id for the configured company is required.
 * @param {string} company_id When not referencing a master list document or collection, a company id for the configured company is required.
 * @param {boolean} deleted Query only deleted or non-deleted metrics. Defaults to both deleted and non-deleted metrics.
 * @returns {Promise<MetricExtended | undefined>} A single mapped metric record.
 */
export const getMetricByName = async (
  master_list: boolean,
  metric_group_name: string,
  metric_name: string,
  group_id?: string,
  company_id?: string,
  deleted?: boolean
): Promise<MetricExtended | undefined> => {
  const metric: MetricExtended = await apollo_client
    .query({
      variables: {
        master_list: master_list,
        metric_group_name: metric_group_name,
        metric_name: metric_name,
        group_id: group_id,
        company_id: company_id,
        deleted: deleted
      },
      query: GET_METRIC_BY_NAME_QUERY,
      fetchPolicy: 'no-cache'
    })
    .then((response) => {
      return response.data.metric_by_name;
    });
  return metric;
};

/**
 * Undo a soft deleted Metric.
 * @param {string} group_id ID of Group to undelete metric for.
 * @param {string} company_id ID of Company to undelete metric for.
 * @param {string} metric_id ID of metric to undelete.
 * @param {string} metric_group_id ID of metric group to find undelete metric in
 * @returns {void}
 */
export const undeleteMetricWithEmissionFactors = async (
  group_id: string,
  company_id: string,
  metric_id: string,
  metric_group_id: string,
  emission_factor_create?: EmissionFactor
): Promise<void> => {
  try {
    await apollo_client
      .mutate({
        mutation: UNDELETE_METRICS_WITH_EMISSION_FACTORS_MUTATOR,
        variables: {
          group_id: group_id,
          company_id: company_id,
          metric_id: metric_id,
          metric_group_id: metric_group_id,
          emission_factor_create: emission_factor_create,
          user_email: auth.currentUser?.email
        }
      })
      .then((response) => {
        return response.data.undeleteMetricWithEmissionFactors;
      });
  } catch (err: unknown) {
    const tracking_id: string = generateUuidV4();
    throw new MetadataError(
      err instanceof Error
        ? err.message
        : 'Error: lib/metric_capture/metric.ts failed on an unknown error while calling undeleteMetricWithEmissionFactors.',
      {
        group_id: group_id,
        company_id: company_id,
        metric_id: metric_id,
        metric_group_id: metric_group_id
      },
      tracking_id
    );
  }
};

/**
 * Sync all company level metrics with data from relative master list metrics.
 * @returns {Promise<void>}
 */
export const syncMasterListMetrics = async (): Promise<void> => {
  try {
    await apollo_client
      .mutate({
        mutation: SYNC_MASTER_LIST_METRICS,
        variables: {}
      })
      .then((response) => {
        return response.data.syncMasterListMetrics;
      });
  } catch (err: unknown) {
    const tracking_id: string = generateUuidV4();
    throw new MetadataError(
      err instanceof Error
        ? err.message
        : 'Error: lib/metric_capture/metric.ts failed on an unknown error while calling syncMasterListMetrics.',
      {},
      tracking_id
    );
  }
};
